/**
 * Invoice mutation hooks using generic factory
 */

import { invoiceApi } from '@/lib/api/invoices.api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateInvoice,
  Invoice,
  UpdateInvoiceInput,
} from '@scrambl/shared'

import { BILLING_AUTHORIZATIONS_QUERY_KEY } from '../queries/useBillingAuthorizationsQuery'
import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'

// Invoice totals, status and linkage drive client-authorization consumption
// (recomputed server-side), so every invoice write refreshes those cards.
const invalidateAuthorizations = {
  additionalInvalidations: [() => BILLING_AUTHORIZATIONS_QUERY_KEY],
}

const mutations = createResourceMutations<
  Invoice,
  CreateInvoice,
  UpdateInvoiceInput
>({
  resourceName: 'Invoice',
  resourceNameSingular: 'invoice',
  queryKeyFactory: queryKeys.invoices,
  api: {
    create: (data: CreateInvoice) => invoiceApi.createInvoice(data),
    update: (id: string, data: UpdateInvoiceInput) =>
      invoiceApi.updateInvoice(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateInvoiceInput
      return invoiceApi.updateInvoice(id, updateData)
    },
    delete: (id: string) => invoiceApi.deleteInvoice(id),
  },
  invalidation: {
    onCreate: invalidateAuthorizations,
    onUpdate: invalidateAuthorizations,
    onDelete: invalidateAuthorizations,
  },
  enableRealtime: false,
})

export const useCreateInvoiceMutation = mutations.useCreateMutation
export const useUpdateInvoiceMutation = mutations.useUpdateMutation
export const useUpdateInvoiceFieldMutation = mutations.useUpdateFieldMutation!
export const useDeleteInvoiceMutation = mutations.useDeleteMutation

// Preserve existing callers while the financial command lives behind its facade.
export { useCreateClientBillingRunMutation } from '@/modules/financials/useCreateBillingRun'

/**
 * Void a sent invoice (SD-160). Voiding frees the time and expenses the
 * invoice billed, so expense lists refresh too.
 */
export function useVoidInvoiceMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      invoiceApi.voidInvoice(id, reason),
    onSuccess: invoice => {
      toast.success(
        invoice.invoiceNumber
          ? `Invoice ${invoice.invoiceNumber} voided`
          : 'Invoice voided',
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.detail(invoice.id),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to void invoice',
      )
    },
  })
}

/**
 * Bulk delete invoices mutation hook
 */
export function useBulkDeleteInvoicesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => invoiceApi.bulkDeleteInvoices(ids),
    onSuccess: result => {
      toast.success(
        `${result.deletedCount} invoice${result.deletedCount === 1 ? '' : 's'} deleted`,
      )
      // Invalidate invoices list
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() })
      queryClient.invalidateQueries({
        queryKey: BILLING_AUTHORIZATIONS_QUERY_KEY,
      })
    },
    onError: error => {
      // The server explains refusals (e.g. only drafts can be deleted).
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to delete invoices',
      )
      console.error('Bulk delete invoices error:', error)
    },
  })
}
